import React from 'react';
import { Button, Table, Popconfirm, message } from 'antd';
import { DeleteOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import { InviteCode, BonusRecord } from '@/types/membership';

interface InvitePromotionProps {
  inviteCodes: InviteCode[];
  bonusRecords: BonusRecord[];
  generatingCode: boolean;
  onGenerateCode: () => void;
  onDeleteCode: (code: string) => void;
}

export default function InvitePromotion({
  inviteCodes,
  bonusRecords,
  generatingCode,
  onGenerateCode,
  onDeleteCode
}: InvitePromotionProps) {
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('邀请码已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败，请手动复制');
    });
  };

  const inviteCodeColumns = [
    {
      title: '邀请码',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <div className="flex items-center space-x-2">
          <span className="font-mono">{code}</span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyCode(code)}
            className="text-brand hover:text-brand"
          />
        </div>
      ),
    },
    {
      title: '返佣比例',
      dataIndex: 'ratio',
      key: 'ratio',
      render: (ratio: number) => `${(ratio * 100).toFixed(1)}%`,
    },
    {
      title: '使用次数',
      dataIndex: 'used_count',
      key: 'used_count',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: InviteCode) => (
        <Popconfirm
          title="确定要删除这个邀请码吗？"
          onConfirm={() => onDeleteCode(record.code)}
          okText="确定"
          cancelText="取消"
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      ),
    },
  ];

  const bonusColumns = [
    {
      title: '订单号',
      dataIndex: 'order_id',
      key: 'order_id',
    },
    {
      title: '返佣金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '邀请码',
      dataIndex: 'invite_code',
      key: 'invite_code',
    },
    {
      title: '被邀请用户',
      dataIndex: 'invited_user',
      key: 'invited_user',
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">我的邀请码</h3>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onGenerateCode}
          loading={generatingCode}
          disabled={inviteCodes.length >= 5}
        >
          生成邀请码
        </Button>
      </div>
      <Table
        columns={inviteCodeColumns}
        dataSource={inviteCodes}
        rowKey="code"
        pagination={false}
        className="user-invite-table"
      />

      <h3 className="text-lg font-semibold mt-6 mb-4">返佣记录</h3>
      <Table
        columns={bonusColumns}
        dataSource={bonusRecords}
        rowKey="order_id"
        pagination={{ pageSize: 10 }}
        className="user-invite-table"
      />
    </div>
  );
}
